import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Star, Sparkles, User } from 'lucide-react';
import { formatPrice, formatDuration, getImageUrl } from '../utils/helpers';

const ProductCard = ({ 
  product, 
  onSelect, 
  onEdit, 
  onToggleAvailability,
  onDelete,
  isAdmin = false,
  index = 0,
  disableToggle = false,
}) => {
  const categoryColors = {
    manicure: 'from-pink-500 to-rose-500',
    extension: 'from-purple-500 to-indigo-500',
    art: 'from-indigo-500 to-blue-500',
    pedicure: 'from-emerald-500 to-teal-500'
  };

  // Usa el nombre de la categoría de la BD
  const categoryColor = categoryColors[product.Categoria?.toLowerCase()] || categoryColors.manicure;
  const disponible = product.Disponible === true || product.Disponible === 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`flex flex-col h-full bg-white rounded-xl shadow-md p-6 ${
        !disponible ? 'opacity-60' : ''
      }`}
      whileHover={{ scale: 1.02, y: -5 }}
    >
      <div className="relative flex-grow">
        <img 
          src={getImageUrl(product.UrlImagen)} 
          alt={product.Nombre}
          className="w-full h-48 object-cover rounded-lg mb-4"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = getImageUrl('');
          }}
        />
        <div className={`absolute top-4 left-4 px-3 py-1 rounded-full bg-gradient-to-r ${categoryColor} text-white text-xs font-semibold uppercase tracking-wider`}>
          {product.Categoria}
        </div>
        {product.ManicuristaNombre && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/90 text-purple-700 text-xs font-semibold shadow-sm flex items-center gap-1">
            <User className="w-3 h-3" />
            {product.ManicuristaNombre}
          </div>
        )}
        {!disponible && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
            <span className="text-white font-bold text-lg">No Disponible</span>
          </div>
        )}
      </div>

        <div className="flex flex-col flex-grow">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-bold text-gray-900">{product.Nombre}</h3>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => {
                const avg = Number(product.CalificacionPromedio || 0);
                const filled = i < Math.round(avg);
                return (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${filled ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                  />
                );
              })}
              <span className="ml-1 text-sm text-gray-600">
                {product.CalificacionPromedio ? Number(product.CalificacionPromedio).toFixed(1) : '—'}
              </span>
              {product.CalificacionCantidad ? (
                <span className="text-xs text-gray-400">({product.CalificacionCantidad})</span>
              ) : null}
            </div>
          </div>

          {product.ManicuristaNombre && (
            <div className="flex items-center gap-1 mb-3 text-sm text-purple-700 font-semibold">
              <User className="w-4 h-4" />
              <span>{product.ManicuristaNombre}</span>
            </div>
          )}

          <p className="text-gray-600 mb-4 leading-relaxed flex-grow">{product.Descripcion}</p>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-gray-500">
            <Clock className="w-4 h-4" />
          </span>
          <span className="text-sm text-gray-600">{formatDuration(product.Duracion)}</span>
          <span className="ml-auto font-bold text-xl">{formatPrice(product.Precio)}</span>
        </div>
      </div>

      <div className="flex gap-3">
        {!isAdmin ? ( 
          <motion.button
            type="button"
            onClick={() => onSelect && onSelect(product)}
            disabled={!disponible}
            className={`mt-auto w-full py-2 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
              disponible
                ? `bg-gradient-to-r ${categoryColor} text-white shadow-lg hover:shadow-xl`
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            whileHover={disponible ? { scale: 1.05 } : {}}
            whileTap={disponible ? { scale: 0.95 } : {}}
          >
            <Sparkles className="w-4 h-4" />
            Reservar
          </motion.button>
        ) : (
          <>
            <motion.button
              type="button"
              onClick={() => onEdit(product)}
              className="mt-auto w-full py-2 rounded-lg font-semibold bg-blue-500 text-white hover:bg-blue-600 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Editar
            </motion.button>
            <motion.button
              type="button"
              onClick={() => !disableToggle && onToggleAvailability(product.Id, product.Disponible)}
              disabled={disableToggle}
              className={`mt-auto w-full py-2 rounded-lg font-semibold transition-all duration-300 ${
                product.Disponible
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-green-500 text-white hover:bg-green-600'
              } ${disableToggle ? 'opacity-60 cursor-not-allowed' : ''}`}
              whileHover={disableToggle ? {} : { scale: 1.05 }}
              whileTap={disableToggle ? {} : { scale: 0.95 }}
            >
              {product.Disponible ? 'Desactivar' : 'Activar'}
            </motion.button>
            <motion.button
              type="button"
              onClick={() => onDelete && onDelete(product.Id)}
              className="mt-auto w-full py-2 rounded-lg font-semibold bg-rose-500 text-white hover:bg-rose-600 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Eliminar
            </motion.button>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;
